/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import Override from 'components/machine/override';
import SectionData from 'components/machine/sectionData';
import React, { ReactElement } from 'react';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { SectionRowElementChart } from 'templates/pubSub/types/TemplateType';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import { usePubSubNode, usePubSubValue } from 'utilities/PubSub/PubSubHooks';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';

interface ISectionElementContentChartProps {
  templateContext: TemplateContext;
  element: SectionRowElementChart;
  cols: string;
  startingNodeId?: NodeIdentifier;
}

export default function SectionElementContentChart(props: ISectionElementContentChartProps): ReactElement | null {
  const valueFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.valueVariable?.variablePath,
  );
  const valueNode = usePubSubNode(valueFilters, props.startingNodeId);
  const value = usePubSubValue(valueNode);
  const chartValue = TemplateHelperPubSub.resolveVariableValue(value, props.element.valueVariable);
  const amount = TemplateHelperPubSub.getChartAmount(chartValue);

  const labelFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.labelVariable?.variablePath,
  );
  const labelNode = usePubSubNode(labelFilters, props.startingNodeId);
  const resolvedLabel = usePubSubValue(labelNode);
  const label = resolvedLabel ?? props.element.label;

  let unitSymbol: string | undefined;
  const unitFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.valueVariable?.hasUnit ? props.element.valueVariable?.variablePath + '.EngineeringUnits' : undefined,
  );
  const unitNode = usePubSubNode(unitFilters, props.startingNodeId);
  const unitValue = usePubSubValue(unitNode);
  if (unitValue) {
    unitSymbol = unitValue.DisplayName;
  }

  let rangeMin: number = 0;
  let rangeMax: number = 100;
  const rangeFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.valueVariable?.hasRange ? props.element.valueVariable?.variablePath + '.EURange' : undefined,
  );
  const rangeNode = usePubSubNode(rangeFilters, props.startingNodeId);
  const rangeValue = usePubSubValue(rangeNode);
  if (rangeValue) {
    rangeMin = rangeValue.Low;
    rangeMax = rangeValue.High;
  }

  if (
    TemplateHelperPubSub.shouldHideOptionalVariable(
      value,
      props.element.valueVariable,
      `SectionElementContentChart:${props.element.id}`,
    )
  ) {
    return null;
  }

  return (
    <SectionData cols={props.cols} label={label}>
      <Override amount={amount} symbol={unitSymbol} max={rangeMax} min={rangeMin} />
    </SectionData>
  );
}
